/* eslint react/prop-types: 0, react/display-name: 0 */

import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import get from 'lodash.get';

// Components
import BuyerAssuranceIcon from 'dibs-vg/dist/react/buyer-assurance';
import CartItemImage from './CartItemImage';
import CartItemTitle from './CartItemTitle';
import CartItemPricing from './CartItemPricing';
import CartItemCTA from './CartItemCTA';
import ItemFolderWrapper from 'dibs-portfolio-folder/exports/ItemFolderWrapper';
import ItemHeartWrapper from 'dibs-portfolio-heart/exports/ItemHeartWrapper';
import CartRemoveItemLink from './CartRemoveItemLink';
import CartQuantitySelect from './CartQuantitySelect';

// Helpers
import folderTracking from './cartFolderTracking';
import tracking from 'dibs-tracking';

// Styles
import styles from './styles/CartItem.scss';

const CartItem = props => {
    const { user, viewer, portfolioItem, onClickProduct } = props;
    const { item } = portfolioItem;
    const { isInProcurement } = item;
    const priceDisplays = get(item, 'displayInfo.pricing') || {};

    const isUnavailable =
        item.isDeleted ||
        item.isNotListed ||
        item.seller.isClosed ||
        item.seller.isPending ||
        item.seller.isSuspended;
    const isPurchasable = !priceDisplays.showSold && !isUnavailable;

    return (
        <div data-tn="cart-item" className={styles.wrapper}>
            <div className="rowFlex">
                <div className="colLg3 colMd5 colXs12">
                    <CartItemImage
                        item={item}
                        isUnavailable={isUnavailable}
                        onClickProductImage={onClickProduct}
                    />
                </div>
                <div className="colLg5 colMd7 colXs12">
                    <div className={styles.middle}>
                        <div className={styles.favorites}>
                            <div className={styles.heartWrapper}>
                                <ItemHeartWrapper
                                    authModalShow={() => {}}
                                    item={item}
                                    loadPortfolioData
                                    onFavorited={() => {
                                        tracking.trackEvent({
                                            action: 'item added',
                                            label: 'cart',
                                            category: 'item favoriting',
                                            additional: {
                                                itemPk: item.serviceId,
                                            },
                                        });
                                    }}
                                    onUnFavorited={() => {
                                        tracking.trackEvent({
                                            action: 'item removed',
                                            label: 'cart',
                                            category: 'item favoriting',
                                            additional: {
                                                itemPk: item.serviceId,
                                            },
                                        });
                                    }}
                                    viewer={viewer}
                                    userId={user.serviceId}
                                    userIds={[user.serviceId]}
                                />
                            </div>
                            <div className={styles.folderWrapper}>
                                <ItemFolderWrapper
                                    authModalTrackingLabel="save item - cart page"
                                    selectedItemImages={[item.firstPhotoWebPath]}
                                    selectedItemTitles={[item.title]}
                                    shouldFillInFolder
                                    singlePortfolioItem
                                    trackingHooks={folderTracking}
                                    folderPortfolioItems={item.folderPortfolioItems}
                                    item={item}
                                    getModalComponent={() => {
                                        return import(/* webpackChunkName: "AddToFolderModalWrapper" */
                                        'dibs-portfolio-folder/src/components/AddToFolderModalWrapper').then(
                                            exported => exported.default
                                        );
                                    }}
                                    userId={user.serviceId}
                                    userIds={[user.serviceId]}
                                />
                            </div>
                        </div>
                        <CartItemTitle
                            item={item}
                            user={user}
                            isUnavailable={isUnavailable}
                            onClick={onClickProduct}
                        />
                        {(isPurchasable || priceDisplays.showOnHold) && (
                            <CartItemPricing user={user} item={item} />
                        )}
                        {isInProcurement && (
                            <div className={styles.inProcurement}>
                                <BuyerAssuranceIcon className={styles.inProcurementIcon} />
                                <FormattedMessage
                                    id="CartItem.inProcurement"
                                    defaultMessage="Requested for Purchasing Service"
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className={`colLg4 colXs12 rowFlex ${styles.buttons}`}>
                    <div className="colXs12 colXsCenter">
                        <CartQuantitySelect item={item} selectClassName={styles.quantitySelect}>
                            {quantitySelected => (
                                <CartItemCTA
                                    item={item}
                                    user={user}
                                    isUnavailable={isUnavailable}
                                    isPurchasable={isPurchasable}
                                    quantitySelected={quantitySelected}
                                />
                            )}
                        </CartQuantitySelect>
                    </div>
                </div>
                <CartRemoveItemLink user={user} viewer={viewer} portfolioItem={portfolioItem} />
            </div>
        </div>
    );
};

CartItem.propTypes = {
    viewer: PropTypes.object.isRequired,
    portfolioItem: PropTypes.object.isRequired,
    onClickProduct: PropTypes.func,
};

export default createFragmentContainer(CartItem, {
    viewer: graphql`
        fragment CartItem_viewer on Viewer
            @argumentDefinitions(loadPortfolioData: { type: "Boolean!", defaultValue: true }) {
            ...CartRemoveItemLink_viewer
            ...ItemHeartWrapper_viewer
                @arguments(loadPortfolioData: $loadPortfolioData, userIds: $userIds)
        }
    `,
    user: graphql`
        fragment CartItem_user on User {
            ...CartRemoveItemLink_user
            ...CartItemPricing_user
            ...CartItemCTA_user
            ...CartItemTitle_user
            serviceId
        }
    `,
    portfolioItem: graphql`
        fragment CartItem_portfolioItem on PortfolioItemV2
            @argumentDefinitions(loadPortfolioData: { type: "Boolean!", defaultValue: true }) {
            ...CartRemoveItemLink_portfolioItem
            item {
                ...CartItemImage_item
                ...CartItemTitle_item
                ...CartItemPricing_item
                ...CartItemCTA_item
                ...ItemFolderWrapper_item
                    @arguments(userId: $userId, loadPortfolioData: $loadPortfolioData)
                ...ItemHeartWrapper_item
                    @arguments(userId: $userId, loadPortfolioData: $loadPortfolioData)
                ...CartQuantitySelect_item
                serviceId
                isNotListed
                isDeleted
                isInProcurement(userId: $userId)
                seller {
                    isClosed
                    isPending
                    isSuspended
                }
                firstPhotoWebPath(size: large)
                title
                displayInfo(buyerId: $userId, page: pdp) {
                    pricing {
                        showOnHold
                        showSold
                    }
                }
            }
        }
    `,
});
